import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService } from '../services/AuthServices';
import welcomeImg from "../assets/welcome.jpg";

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [timer, setTimer] = useState(30);
  const [isSendOtpDisabled, setIsSendOtpDisabled] = useState(false);
  const [otpSentOnce, setOtpSentOnce] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (timer > 0 && isSendOtpDisabled) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    if (timer === 0) {
      setIsSendOtpDisabled(false);
    }
  }, [timer, isSendOtpDisabled]);

  const sendOtp = async () => {
    try {
      if (!email) {
        toast.error("Email is required to send OTP.");
        return;
      }
      const otpData = { email };
      await authService.sendOtp(otpData);

      toast.success("OTP sent successfully!");
      setOtpSentOnce(true);
      setIsSendOtpDisabled(true);
      setTimer(30);
      setStep(2);
    } catch (err) {
      console.error("OTP send error", err);
      toast.error("Failed to send OTP. Please try again.");
    }
  };

  const verifyOtp = async () => {
    try {
      const enteredOtp = otp.join('');
      const verifyData = { email, otp: enteredOtp };
      await authService.verifyOtp(verifyData);

      toast.success("OTP verified successfully.");
      setStep(3);
    } catch (err) {
      toast.error("Invalid OTP. Please try again.");
    }
  };

  const handleOtpChange = (index, value) => {
    const updatedOtp = [...otp];
    updatedOtp[index] = value.slice(-1);
    setOtp(updatedOtp);
  };

  const resendOtp = () => {
    if (!isSendOtpDisabled) {
      sendOtp();
    } else {
      toast.warning(`Please wait ${timer}s to resend OTP.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
        toast.error("Passwords do not match.");
        return;
    }

    try {
        await authService.resetPassword({ email, password });
        toast.success('Password changed successfully. Redirecting to login...');
        setTimeout(() => {
            navigate('/user/login');
        }, 2000);
    } catch (error) {
        const errorMessage = error.response?.data?.message || 'Error occurred. Please try again.';
        toast.error(errorMessage);
    }
};

  return (
    <div id="cryptoon-layout" className="theme-orange">
      <div className="main p-2 py-3 p-xl-5">
        <div className="body d-flex">
          <div className="container-xxl">
            <div className="row g-3">
              <div className="col-lg-6 d-flex justify-content-center align-items-center auth-h100">
                <div className="d-flex flex-column">
                  <h1>Forgot Password</h1>
                  <div className="tab-content mt-4 mb-3">
                    <div className="card">
                      <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                          {step === 1 && (
                            <>
                              <div className="mb-3">
                                <label className="form-label fs-6">Email address</label>
                                <input
                                  type="email"
                                  className="form-control"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value)}
                                  placeholder="Enter your email"
                                  required
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={sendOtp}
                              >
                                Send OTP
                              </button>
                            </>
                          )}

                          {step === 2 && (
                            <>
                              <div className="row g-1 mt-3">
                                {otp.map((val, idx) => (
                                  <div className="col" key={idx}>
                                    <input
                                      type="text"
                                      className="form-control form-control-lg text-center"
                                      placeholder="-"
                                      autoComplete="off"
                                      maxLength={1}
                                      value={val}
                                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                                    />
                                  </div>
                                ))}
                              </div>
                              <div className="text-center mt-3">
                                <button
                                  type="button"
                                  className="btn btn-primary w-100"
                                  onClick={verifyOtp}
                                >
                                  Verify OTP
                                </button>
                              </div>
                              <div className="text-center mt-3">
                                <button
                                  type="button"
                                  className="btn btn-link"
                                  onClick={resendOtp}
                                  disabled={isSendOtpDisabled}
                                >
                                  {isSendOtpDisabled ? `Resend OTP (${timer}s)` : "Resend OTP"}
                                </button>
                              </div>
                            </>
                          )}

                          {step === 3 && (
                            <>
                              <div className="mb-3">
                                <label className="form-label fs-6">New Password</label>
                                <input
                                  type="password"
                                  className="form-control"
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  placeholder="Enter new password"
                                  required
                                />
                              </div>
                              <div className="mb-3">
                                <label className="form-label fs-6">Confirm Password</label>
                                <input
                                  type="password"
                                  className="form-control"
                                  value={confirmPassword}
                                  onChange={(e) => setConfirmPassword(e.target.value)}
                                  placeholder="Confirm your password"
                                  required
                                />
                              </div>
                              <button type="submit" className="btn btn-primary w-100">
                                Submit
                              </button>
                            </>
                          )}

                          {error && <p className="text-danger mt-3">{error}</p>}
                          {success && <p className="text-success mt-3">{success}</p>}
                        </form>
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="small mb-0">
                      Already have an account? <Link to="/user/login">Login</Link>
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <img src={welcomeImg} alt="Welcome" className="img-fluid" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
